/* Australian commerce and real-product gallery for TLC's bundle landing page. */
(() => {
  'use strict';
  const SCRIPT_KEY = Symbol.for('tlc.bundle.au.initializer');
  if (window[SCRIPT_KEY]) return;
  window[SCRIPT_KEY] = true;
  const instances = new WeakMap();
  const SIZES = ['Single', 'King Single', 'Double', 'Queen', 'King', 'Super King', 'Split King'];
  const idOf = value => /^[1-9]\d*$/.test(String(value ?? '')) ? String(value) : '';
  const urlOf = (value, sameOrigin = false) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.href);
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return '';
      if (sameOrigin && url.origin !== location.origin) return '';
      return url.href;
    } catch (_) { return ''; }
  };
  const imageUrl = (source, width) => {
    const safe = urlOf(source);
    if (!safe) return '';
    const url = new URL(safe);
    if (width) url.searchParams.set('width', String(width));
    return url.href;
  };
  const sizeOf = variant => (Array.isArray(variant.options) ? variant.options : []).find(option => SIZES.includes(option))
    || String(variant.title || '').split(' / ').find(option => SIZES.includes(option)) || '';
  const element = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = content;
    return node;
  };

  class AUBundle {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.cache = new Map();
      this.pending = new Map();
      this.size = 'Queen';
      this.quantity = 1;
      this.state = 'idle';
      this.request = 0;
      this.photoIndex = 0;
      this.message = '';
    }
    $(selector) { return this.root.querySelector(selector); }
    $$(selector) { return [...this.root.querySelectorAll(selector)]; }
    listen(target, name, handler, options = {}) {
      target?.addEventListener(name, handler, {...options, signal: this.controller.signal});
    }
    text(selector, content) { const node = this.$(selector); if (node) node.textContent = content; }
    money(value) {
      if (this.currency === 'AUD') return `A$${this.numberFormatter.format(value / 100)}`;
      return this.moneyFormatter.format(value / 100);
    }
    status(content) { this.message = content; this.text('#tl-status', content); }
    active() { return this.root.isConnected && !this.controller.signal.aborted; }

    start() {
      try { this.config = JSON.parse(document.getElementById('tlc-au-catalogue')?.textContent || '{}'); }
      catch (_) { this.status('Options could not be loaded. Please use the product link below.'); return; }
      if (!this.config || typeof this.config !== 'object') { this.status('Options could not be loaded. Please use the product link below.'); return; }
      try {
        this.currency = this.config.currency || 'AUD';
        this.moneyFormatter = new Intl.NumberFormat('en-AU', {style: 'currency', currency: this.currency, currencyDisplay: 'code'});
        this.numberFormatter = new Intl.NumberFormat('en-AU', {minimumFractionDigits: 2, maximumFractionDigits: 2});
      } catch (_) { this.status('Currency options could not be loaded. Please use the product link below.'); return; }
      this.descriptors = Array.isArray(this.config.products) ? this.config.products.filter(item =>
        item && /^[a-z0-9][a-z0-9-]*$/.test(item.handle) && typeof (item.colour || item.name) === 'string') : [];
      if (!this.descriptors.length) { this.status('Options could not be loaded. Please use the product link below.'); return; }
      this.base = urlOf(this.config.rootUrl || '/', true);
      this.cartAddUrl = urlOf(this.config.cartAddUrl, true);
      this.cartUrl = urlOf(this.config.cartUrl, true);
      if (!this.base || !this.cartAddUrl || !this.cartUrl) { this.status('Please use the product page to purchase this set.'); return; }
      this.base = this.base.replace(/\/?$/, '/');
      this.defaultHandle = this.descriptors.some(item => item.handle === this.config.defaultHandle)
        ? this.config.defaultHandle : this.descriptors[0].handle;
      this.buildColours();
      this.listen(this.$('.tl-colors'), 'click', event => {
        const button = event.target.closest('button[data-product]');
        if (button && this.root.contains(button)) this.choose(button.dataset.product);
      });
      this.listen(this.$('#tl-sizes'), 'click', event => {
        const button = event.target.closest('button[data-size]');
        if (!button || button.disabled || this.locked()) return;
        this.size = button.dataset.size;
        this.status('');
        this.normaliseQuantity();
        this.render();
        this.refreshContents();
        this.$$('[data-size]').find(item => item.dataset.size === this.size)?.focus({preventScroll: true});
      });
      this.listen(this.$('#tl-minus'), 'click', () => this.changeQuantity(-1));
      this.listen(this.$('#tl-plus'), 'click', () => this.changeQuantity(1));
      this.listen(this.$('#tl-add'), 'click', event => { event.preventDefault(); event.stopPropagation(); this.add(); });
      this.setupGalleryControls();
      this.positionGallery();
      this.listen(window, 'pageshow', event => {
        if (!event.persisted || !this.active()) return;
        // Revalidate stock/price after navigating back; never repeat a cart mutation.
        this.cache.clear();
        if (this.state === 'checking' || this.state === 'pending') this.uncertain();
        if (this.state === 'idle') this.choose(this.handle || this.defaultHandle);
        else this.render();
      });
      this.root.dataset.auReady = 'true';
      this.choose(this.defaultHandle);
    }

    buildColours() {
      const container = this.$('.tl-colors');
      if (!container) return;
      const existing = new Map(this.$$('[data-product]').map(button => [button.dataset.product, button]));
      container.replaceChildren(...this.descriptors.map(item => {
        const name = item.colour || item.name;
        const button = element('button', 'tl-color');
        button.type = 'button'; button.dataset.product = item.handle;
        button.setAttribute('aria-label', `Choose ${name} bedding`);
        button.setAttribute('aria-pressed', 'false');
        const source = imageUrl(item.image || existing.get(item.handle)?.querySelector('img')?.getAttribute('src'), 180);
        if (source) {
          const image = element('img'); image.src = source; image.alt = ''; image.loading = 'lazy'; image.decoding = 'async';
          button.append(image);
        } else {
          const swatch = element('span', 'tl-au-swatch');
          swatch.setAttribute('aria-hidden', 'true');
          if (/^#[0-9a-f]{3,8}$/i.test(item.swatch || '')) swatch.style.backgroundColor = item.swatch;
          button.append(swatch);
        }
        button.append(element('span', '', name));
        return button;
      }));
    }

    productUrl(handle) { return new URL(`products/${encodeURIComponent(handle)}`, this.base).href; }
    async load(handle, fresh = false) {
      if (!this.descriptors.some(item => item.handle === handle)) throw new Error('Unknown colour');
      const cached = this.cache.get(handle);
      if (!fresh && cached && Date.now() - cached.at < 30000) return cached.product;
      if (this.pending.has(handle)) return this.pending.get(handle);
      const promise = (async () => {
        const response = await fetch(`${this.productUrl(handle)}.js`, {headers: {Accept: 'application/json'}, credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        if (!product || product.handle !== handle || !Array.isArray(product.variants)
          || !product.variants.length || !product.variants.every(v => v && idOf(v.id) && typeof v.price === 'number')) throw new Error('Invalid product options');
        this.cache.set(handle, {product, at: Date.now()});
        if (handle === this.defaultHandle && this.active()) {
          const minimum = Math.min(...product.variants.filter(v => v.available).map(v => v.price));
          const hero = document.getElementById('au-hero-price');
          if (hero) hero.textContent = Number.isFinite(minimum) ? `From ${this.money(minimum)}` : 'Choose your colour and size';
        }
        return product;
      })();
      this.pending.set(handle, promise);
      try { return await promise; } finally { if (this.pending.get(handle) === promise) this.pending.delete(handle); }
    }

    async choose(handle) {
      if (this.locked() || !this.descriptors.some(item => item.handle === handle)) return;
      const token = ++this.request;
      this.handle = handle;
      this.descriptor = this.descriptors.find(item => item.handle === handle);
      this.product = null;
      this.status('');
      this.text('#tl-color-name', this.descriptor.colour || this.descriptor.name);
      const link = this.$('#tl-product-link'); if (link) link.href = this.productUrl(handle);
      this.clearGallery();
      this.render();
      try {
        const product = await this.load(handle);
        if (token !== this.request || !this.active()) return;
        this.product = product;
        this.normaliseQuantity();
        this.render(); this.buildGallery();
      } catch (_) {
        if (token !== this.request || !this.active()) return;
        this.text('#tl-price', 'Price unavailable');
        this.text('#tl-add span', 'Please use the product page');
        this.status('Current options could not be loaded. Use “View full product details” below to choose your set.');
      }
    }

    variant() { return this.product?.variants.find(item => sizeOf(item) === this.size); }
    rules() {
      const rule = this.variant()?.quantity_rule || {};
      const min = rule.min == null ? 1 : Number(rule.min);
      const max = rule.max == null ? Infinity : Number(rule.max);
      const increment = rule.increment == null ? 1 : Number(rule.increment);
      const valid = Number.isSafeInteger(min) && min >= 1 && (max === Infinity || Number.isSafeInteger(max))
        && max >= min && Number.isSafeInteger(increment) && increment >= 1;
      return {min, max, increment, valid};
    }
    validQuantity() {
      const rule = this.rules();
      return rule.valid && Number.isSafeInteger(this.quantity) && this.quantity >= rule.min
        && this.quantity <= rule.max && this.quantity % rule.increment === 0;
    }
    normaliseQuantity() {
      const rule = this.rules();
      if (!this.variant() || !rule.valid || this.validQuantity()) return;
      const next = Math.ceil(rule.min / rule.increment) * rule.increment;
      if (next <= rule.max) {
        this.quantity = next;
        this.status(`Quantity updated to ${next} to match this set’s purchase requirements.`);
      }
    }
    locked() { return ['checking', 'pending', 'uncertain', 'success'].includes(this.state); }
    canBuy() {
      const variant = this.variant();
      return Boolean(variant?.available === true && idOf(variant.id) && Number.isSafeInteger(variant.price) && variant.price >= 0
        && Number.isSafeInteger(variant.price * this.quantity) && !this.product.requires_selling_plan
        && !variant.requires_selling_plan && this.validQuantity());
    }
    changeQuantity(direction) {
      if (this.locked() || !this.variant()) return;
      const rules = this.rules(), next = this.quantity + direction * rules.increment;
      if (!rules.valid || !Number.isSafeInteger(next) || next < rules.min || next > rules.max) return;
      this.quantity = next; this.status(''); this.render();
    }

    render() {
      const variant = this.variant(), busy = this.state === 'checking' || this.state === 'pending';
      for (const button of this.$$('[data-product]')) {
        button.disabled = this.locked();
        button.setAttribute('aria-pressed', String(button.dataset.product === this.handle));
      }
      const sizes = this.$('#tl-sizes');
      if (sizes) sizes.replaceChildren(...SIZES.map(size => {
        const match = this.product?.variants.find(item => sizeOf(item) === size);
        const button = element('button', 'tl-size', size); button.type = 'button'; button.dataset.size = size;
        button.disabled = this.locked() || !match || match.available !== true;
        button.setAttribute('aria-pressed', String(size === this.size));
        button.setAttribute('aria-label', `${size}${!this.product ? ' — loading' : !match ? ' — not offered' : !match.available ? ' — sold out' : ''}`);
        return button;
      }));
      this.text('#tl-price', variant ? this.money(variant.price * this.quantity) : this.product ? 'Unavailable in this size' : 'Checking price…');
      const cta = this.$('#tl-add');
      if (cta) { cta.disabled = this.locked() || !this.canBuy(); cta.setAttribute('aria-busy', String(busy)); }
      let buttonText = !this.product ? 'Loading your sheet set…' : !variant ? 'Unavailable in this size'
        : !variant.available ? 'Sold out in this size' : !this.canBuy() ? 'Choose options on product page'
        : `Add to cart — ${this.money(variant.price * this.quantity)}`;
      if (this.state === 'checking') buttonText = 'Checking your set…';
      if (this.state === 'pending') buttonText = 'Adding your set…';
      if (this.state === 'uncertain') buttonText = 'Check your cart';
      if (this.state === 'success') buttonText = 'Added to your cart';
      this.text('#tl-add span', buttonText);
      const savings = this.$('#tl-add-savings'); if (savings) { savings.hidden = true; savings.textContent = ''; }
      this.text('#tl-quantity', String(this.quantity));
      const rule = this.rules();
      const minus = this.$('#tl-minus'), plus = this.$('#tl-plus');
      if (minus) minus.disabled = this.locked() || !variant || !rule.valid || this.quantity - rule.increment < rule.min;
      if (plus) plus.disabled = this.locked() || !variant || !rule.valid || this.quantity + rule.increment > rule.max;
      const link = this.$('#tl-product-link');
      if (link && this.handle) link.href = this.productUrl(this.handle) + (variant ? `?variant=${encodeURIComponent(idOf(variant.id))}` : '');
      const cartLink = this.$('#tl-cart-link');
      if (cartLink) { cartLink.href = this.cartUrl; cartLink.hidden = !['uncertain', 'success'].includes(this.state); }
      if (!this.message && this.product) {
        if (!variant) this.text('#tl-status', `${this.size} is not offered in this colour. Choose another size or colour.`);
        else if (!variant.available) this.text('#tl-status', `${this.descriptor.colour || this.descriptor.name} in ${this.size} is sold out. Choose another size or colour.`);
        else if (!this.canBuy()) this.text('#tl-status', 'This set has additional purchase requirements. Use the product page to choose your options.');
      }
    }

    uncertain() {
      this.state = 'uncertain';
      this.status('We could not confirm whether your set was added. Check your cart before trying again. Reload this page only after checking your cart.');
    }
    async add() {
      if (this.locked() || !this.canBuy()) return;
      const handle = this.handle, size = this.size, requestedId = idOf(this.variant().id);
      const requestedQuantity = this.quantity, shownPrice = this.variant().price;
      this.status(''); this.state = 'checking'; this.render();
      try {
        const current = await this.load(handle, true);
        if (!this.active()) return;
        this.product = current;
        const currentVariant = this.variant();
        if (!this.canBuy() || idOf(currentVariant?.id) !== requestedId || this.size !== size) {
          this.state = 'idle'; this.status('Availability or purchase requirements have changed. Please review your selection.'); this.render(); return;
        }
        if (currentVariant.price !== shownPrice) {
          this.state = 'idle'; this.status('The current price has changed. Please review the updated total before adding your set.'); this.render(); return;
        }
      } catch (_) {
        if (!this.active()) return;
        this.state = 'idle'; this.status('We could not check current availability. Nothing was added. Please try again or use the product page.'); this.render(); return;
      }
      this.state = 'pending'; this.render();
      try {
        const response = await fetch(this.cartAddUrl, {method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items: [{id: requestedId, quantity: requestedQuantity}]})});
        let result;
        try { result = await response.json(); } catch (_) { this.uncertain(); }
        const confirmed = response.ok && Array.isArray(result?.items) && result.items.some(item =>
          idOf(item?.variant_id || item?.id) === requestedId && Number.isSafeInteger(Number(item.quantity))
          && Number(item.quantity) >= requestedQuantity);
        if (confirmed) { this.state = 'success'; this.status('Added. Opening your cart, where eligible offers are applied.'); }
        else if (result && [400, 404, 422].includes(response.status)) {
          // For quantities above one, Shopify can add the available portion while returning 422.
          if (requestedQuantity > 1) this.uncertain();
          else {
            this.state = 'idle';
            this.status(typeof result.description === 'string' ? result.description
              : typeof result.message === 'string' ? result.message : 'This set could not be added. Review your selection.');
          }
        } else this.uncertain();
      } catch (_) { this.uncertain(); }
      if (!this.active()) return;
      this.render();
      if (this.state === 'success') location.assign(this.cartUrl);
    }

    photos() {
      const media = Array.isArray(this.product?.media) ? this.product.media.filter(item => item.media_type === 'image') : [];
      const fromMedia = media.map(item => ({src: urlOf(item.src || item.preview_image?.src), alt: typeof item.alt === 'string' ? item.alt : ''})).filter(item => item.src);
      if (fromMedia.length) return fromMedia;
      return (this.product?.images || []).map(source => ({src: urlOf(typeof source === 'string' ? source : source.src), alt: ''})).filter(item => item.src);
    }
    photo(source, eager = false) {
      const image = element('img', 'tm-photo');
      image.src = imageUrl(source.src, 1000); image.alt = source.alt || `${this.descriptor.colour || this.descriptor.name} bedding`;
      image.srcset = [400, 600, 800, 1000, 1400].map(width => `${imageUrl(source.src, width)} ${width}w`).join(', ');
      image.sizes = '(max-width:700px) calc(100vw - 36px), 50vw'; image.width = 1400; image.height = 1400;
      image.loading = eager ? 'eager' : 'lazy'; image.decoding = 'async';
      return image;
    }
    head(eyebrow, title, description) {
      const head = element('header', 'tm-head');
      head.append(element('small', '', eyebrow), element('h3', '', title), element('p', '', description));
      return head;
    }
    call(className, title, description) {
      const call = element('div', `tm-call ${className}`, title);
      if (description) call.append(element('small', '', description));
      return call;
    }
    foot() {
      const foot = element('div', 'tm-foot'), dot = element('i', 'tm-color-dot'), text = element('span');
      dot.setAttribute('aria-hidden', 'true');
      if (/^#[0-9a-f]{3,8}$/i.test(this.descriptor.swatch || '')) dot.style.backgroundColor = this.descriptor.swatch;
      text.append(element('b', '', this.descriptor.colour || this.descriptor.name), element('small', '', 'Bamboo-cotton sheet set'));
      foot.append(dot, text); return foot;
    }
    clearGallery() {
      const market = this.$('.tl-market'); if (market) { market.hidden = true; market.replaceChildren(); }
      const image = this.$('.tl-main-image');
      if (image) {
        const source = imageUrl(this.descriptor.image, 1000);
        image.removeAttribute('srcset'); image.style.display = source ? '' : 'none';
        if (source) { image.src = source; image.alt = `${this.descriptor.colour || this.descriptor.name} bedding`; }
      }
      this.$('.tl-thumbs')?.replaceChildren(); this.$('.tl-gallery-dots')?.replaceChildren();
      this.slides = [];
    }
    buildGallery() {
      const photos = this.photos();
      if (!photos.length) return;
      const first = photos[0], texture = photos.find(item => /texture|fabric|folds/i.test(item.alt)) || photos[1] || first;
      const grip = photos.find(item => /hand.*(loop|strap)|(pull|tug).*(loop|strap)/i.test(item.alt))
        || photos.find(item => /loop|strap/i.test(item.alt)) || first;
      const zip = photos.find(item => /zip/i.test(item.alt)) || first;
      const corner = photos.find(item => /mattress corner|fitted.*corner/i.test(item.alt)) || first;
      const contents = photos.find(item => /contents/i.test(item.src)) || first;
      const partnerPhoto = {src: 'https://theladcollective.com/cdn/shop/files/watch_campaign_banner.webp?v=1781531737',
        alt: 'Robert Irwin in The Lad Collective’s Australian campaign'};
      const narrative = (label, className, photo, eyebrow, heading, description, callTitle, callText) => ({
        label, className, photo, build: () => {
          const nodes = [this.photo(photo), this.head(eyebrow, heading, description), this.foot()];
          if (callTitle) nodes.push(this.call('tm-a', callTitle, callText));
          return nodes;
        }
      });
      this.slides = [
        narrative('EasyGrip', 'tm-grip', grip, '01 / EASYGRIP CORNER LOOPS', 'Right corner. First time.', 'Find the label. Pull the loop. Make the bed.', 'Get a grip.', 'Corner loops make pulling the fitted sheet into place easier.'),
        narrative('Why TLC', 'tm-overview', first, '02 / LITTLE DETAILS. LESS EFFORT.', 'Your bed, made easier.', 'Labelled corners. A centred top sheet. Hidden-zip pillowcases.'),
        {label: 'Your sheet set', className: 'tm-bundle', photo: contents, build: () => [this.photo(contents), this.foot()]},
        narrative('The fabric', 'tm-fabric', texture, '03 / THE FABRIC', 'Soft where it counts.', 'Everyday comfort, down to the blend.', '60% bamboo. 40% cotton.', 'One soft, breathable blend.'),
        narrative('Hidden zips', 'tm-zip', zip, '04 / HIDDEN-ZIP PILLOWCASES', 'Pillows stay put.', 'Zip it up. Keep your pillow tucked in.', 'Hidden zip. Clean finish.', 'A discreet closure at the edge.'),
        narrative('Your bed, sorted', 'tm-overview', corner, '05 / LESS WRESTLING', 'Make sheet day easier.', 'Labelled corners and loops help you pull the fitted sheet into place.'),
        {label: 'In good company', className: 'tm-social', photo: partnerPhoto, build: () => {
          const caption = element('p', 'tm-quote', 'Robert Irwin × The Lad Collective');
          const link = element('a', '', 'Meet our brand partner');
          link.href = new URL('pages/campaign-story', this.base).href;
          caption.append(element('small', '', ''), link);
          return [this.photo(partnerPhoto), this.head('06 / IN GOOD COMPANY', 'Clever bedding. A familiar face.', 'Meet The Lad Collective’s brand partner.'), caption];
        }},
        {label: 'Included in your set', className: 'tm-contents', photo: contents, build: () => this.contentsSlide(contents)},
        {label: '90-night trial', className: 'tm-trial', photo: texture, build: () => {
          const call = this.call('tm-a', '90 nights to sleep on it.', 'Trial on eligible sheets. Terms apply.');
          const link = element('a', '', 'Read trial terms'); link.href = new URL('pages/returns-exchanges', this.base).href;
          call.append(element('small', '', ''), link);
          return [this.photo(texture), this.head('07 / GET PROPERLY ACQUAINTED', 'Give your sheets 90 nights.', 'A little time to find your comfort zone.'), call, this.foot()];
        }}
      ];
      // Preserve every official product photo, including details not used in the narrative.
      photos.forEach((photo, index) => this.slides.push({label: `Photo ${index + 1}`, className: 'tm-bundle', photo,
        build: () => [this.photo(photo), this.foot()]}));
      let dots = this.$('.tl-gallery-dots');
      if (!dots) { dots = element('div', 'tl-gallery-dots'); this.$('.tl-thumbs')?.before(dots); }
      if (dots) { dots.setAttribute('role', 'group'); dots.setAttribute('aria-label', 'Gallery image navigation'); }
      const buttons = (thumbnail = false) => this.slides.map((slide, index) => {
        const button = element('button'); button.type = 'button'; button.dataset.index = String(index);
        button.setAttribute('aria-label', `${this.descriptor.colour || this.descriptor.name}: ${slide.label}, ${index + 1} of ${this.slides.length}`);
        if (thumbnail) {
          const image = element('img'); image.src = imageUrl(slide.photo.src, 180); image.alt = ''; image.loading = 'lazy';
          button.append(image, element('span', '', slide.label));
        }
        return button;
      });
      dots?.replaceChildren(...buttons()); this.$('.tl-thumbs')?.replaceChildren(...buttons(true));
      this.showSlide(0);
    }
    contentsSlide(photo) {
      const includes = element('div', 'tm-set-includes');
      includes.append(element('h3', '', 'Your set includes'),
        element('p', '', this.size === 'Split King' ? '2 loop-strap fitted sheets' : '1 loop-strap fitted sheet'),
        element('p', '', '1 centre-aligned top sheet'),
        element('p', '', ['Single', 'King Single'].includes(this.size) ? '1 hidden-zip pillowcase' : '2 hidden-zip pillowcases'));
      const extras = element('div', 'tm-gift-panel');
      extras.append(element('h3', '', 'A little extra TLC'), element('p', '', 'Matching eye mask and a 7-week supply of scented laundry sheets included with your sheet set.'),
        element('p', '', 'Quilt cover sold separately.'));
      return [this.photo(photo), includes, extras, this.foot()];
    }
    refreshContents() { if (this.slides?.[this.photoIndex]?.label === 'Included in your set') this.showSlide(this.photoIndex); }
    showSlide(index) {
      if (!this.slides?.length) return;
      this.photoIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      const slide = this.slides[this.photoIndex], market = this.$('.tl-market');
      if (!market) return;
      market.className = `tl-market ${slide.className}`; market.replaceChildren(...slide.build()); market.hidden = false;
      const main = this.$('.tl-main-image'); if (main) main.style.display = 'none';
      for (const button of this.$$('.tl-thumbs button, .tl-gallery-dots button')) button.setAttribute('aria-pressed', String(Number(button.dataset.index) === this.photoIndex));
    }
    setupGalleryControls() {
      this.listen(this.$('#tlc-landing'), 'click', event => {
        const button = event.target.closest('button[data-index]');
        if (button) this.showSlide(Number(button.dataset.index));
      });
      this.listen(this.$('#tlc-landing'), 'keydown', event => {
        const button = event.target.closest('button[data-index]');
        if (!button || !['ArrowRight', 'ArrowLeft'].includes(event.key) || !this.slides?.length) return;
        event.preventDefault(); this.showSlide(this.photoIndex + (event.key === 'ArrowRight' ? 1 : -1));
        button.parentElement.children[this.photoIndex]?.focus();
      });
      let start = null;
      this.listen(this.$('.tl-stage'), 'touchstart', event => {
        start = event.touches.length === 1 ? {x: event.touches[0].clientX, y: event.touches[0].clientY} : null;
      }, {passive: true});
      this.listen(this.$('.tl-stage'), 'touchcancel', () => { start = null; }, {passive: true});
      this.listen(this.$('.tl-stage'), 'touchend', event => {
        if (!start || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - start.x, dy = event.changedTouches[0].clientY - start.y; start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) this.showSlide(this.photoIndex + (dx < 0 ? 1 : -1));
      }, {passive: true});
    }
    positionGallery() {
      const shop = this.$('#shop'), copy = this.$('.shop-copy'), visual = this.$('.shop-gallery');
      if (!shop || !copy || !visual || !window.matchMedia) return;
      const media = window.matchMedia('(max-width:700px)');
      const position = () => { if (media.matches) copy.querySelector('.tlc-au-rating')?.after(visual); else shop.insertBefore(visual, copy); };
      this.listen(media, 'change', position); position();
    }
    dispose() {
      this.request += 1; this.controller.abort();
      if (this.state === 'pending') this.uncertain();
    }
  }

  const initialize = () => {
    const root = document.getElementById('tlc-bundle-buybox');
    if (!root || instances.has(root)) return;
    const instance = new AUBundle(root); instances.set(root, instance); instance.start();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    const root = document.getElementById('tlc-bundle-buybox');
    if (root && event.target.contains(root)) instances.get(root)?.dispose();
  });
})();
